import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import { EnhancedClass } from '../../types/class-data';
import { findClassById } from '../../utils/classDataLoader';
import { findEquipment } from '../../utils/contentLibraryHelpers';
import './StartingEquipment.css';

interface StartingEquipmentProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

interface ResolvedEquipment {
  primaryWeapon?: any;
  secondaryWeapon?: any;
  armor?: any;
}

const StartingEquipment: React.FC<StartingEquipmentProps> = ({
  characterData,
  onDataChange,
}) => {
  const [classData, setClassData] = useState<EnhancedClass | null>(null);
  const [equipmentChoices, setEquipmentChoices] = useState(
    characterData.selectedEquipment || { primaryWeapon: false, secondaryWeapon: false, armor: false }
  );
  const [resolvedEquipment, setResolvedEquipment] = useState<ResolvedEquipment>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadClassData = async () => {
      if (characterData.class) {
        try {
          const data = await findClassById(characterData.class);
          setClassData(data);

          // Resolve equipment details
          if (data?.suggestedEquipment) {
            const resolved: ResolvedEquipment = {};

            if (data.suggestedEquipment.primaryWeapon) {
              try {
                resolved.primaryWeapon = await findEquipment(data.suggestedEquipment.primaryWeapon);
              } catch (error) {
                console.error('Failed to resolve primary weapon:', error);
              }
            }

            if (data.suggestedEquipment.secondaryWeapon) {
              try {
                resolved.secondaryWeapon = await findEquipment(data.suggestedEquipment.secondaryWeapon);
              } catch (error) {
                console.error('Failed to resolve secondary weapon:', error);
              }
            }

            if (data.suggestedEquipment.armor) {
              try {
                resolved.armor = await findEquipment(data.suggestedEquipment.armor);
              } catch (error) {
                console.error('Failed to resolve armor:', error);
              }
            }

            setResolvedEquipment(resolved);
          }
        } catch (error) {
          console.error('Failed to load class data for equipment selection:', error);
        }
      }
      setLoading(false);
    };

    loadClassData();
  }, [characterData.class]);

  const handleEquipmentToggle = (equipmentType: 'primaryWeapon' | 'secondaryWeapon' | 'armor', selected: boolean) => {
    const newChoices = { ...equipmentChoices, [equipmentType]: selected };
    setEquipmentChoices(newChoices);
    onDataChange({ selectedEquipment: newChoices });
  };

  if (loading) {
    return <div className="starting-equipment__loading">Loading starting equipment...</div>;
  }

  if (!classData?.suggestedEquipment) {
    return (
      <div className="starting-equipment__empty">
        <p>This class doesn't have suggested starting equipment.</p>
        <p>You can proceed to the next step.</p>
      </div>
    );
  }

  const { suggestedEquipment } = classData;

  return (
    <div className="starting-equipment">
      <p className="starting-equipment__description">
        Your class suggests some starting equipment to get you started. You can choose to add these items 
        to your inventory when your character is created.
      </p>

      <div className="starting-equipment__content">
        {/* Suggested Equipment */}
        <div className="equipment-section">
          <h3>Suggested Equipment</h3>
          
          {suggestedEquipment.primaryWeapon && (
            <div className="equipment-choice">
              <div className="equipment-info">
                <h4>Primary Weapon</h4>
                <div className="equipment-details">
                  {resolvedEquipment.primaryWeapon ? (
                    <>
                      <div className="equipment-name">{resolvedEquipment.primaryWeapon.name}</div>
                      {resolvedEquipment.primaryWeapon.trait && (
                        <div className="equipment-stat">Trait: {resolvedEquipment.primaryWeapon.trait}</div>
                      )}
                      {resolvedEquipment.primaryWeapon.range && (
                        <div className="equipment-stat">Range: {resolvedEquipment.primaryWeapon.range}</div>
                      )}
                      {resolvedEquipment.primaryWeapon.damage && (
                        <div className="equipment-stat">Damage: {resolvedEquipment.primaryWeapon.damage} {resolvedEquipment.primaryWeapon.damageType}</div>
                      )}
                      {resolvedEquipment.primaryWeapon.burden && (
                        <div className="equipment-stat">Burden: {resolvedEquipment.primaryWeapon.burden}</div>
                      )}
                      {resolvedEquipment.primaryWeapon.feature && (
                        <div className="equipment-feature">Feature: {resolvedEquipment.primaryWeapon.feature}</div>
                      )}
                    </>
                  ) : (
                    <div className="equipment-loading">Loading weapon details...</div>
                  )}
                </div>
              </div>
              <label className="equipment-toggle">
                <input
                  type="checkbox"
                  checked={equipmentChoices.primaryWeapon}
                  onChange={(e) => handleEquipmentToggle('primaryWeapon', e.target.checked)}
                />
                <span className="toggle-text">Add to inventory</span>
              </label>
            </div>
          )}

          {suggestedEquipment.secondaryWeapon && (
            <div className="equipment-choice">
              <div className="equipment-info">
                <h4>Secondary Weapon</h4>
                <div className="equipment-details">
                  {resolvedEquipment.secondaryWeapon ? (
                    <>
                      <div className="equipment-name">{resolvedEquipment.secondaryWeapon.name}</div>
                      {resolvedEquipment.secondaryWeapon.trait && (
                        <div className="equipment-stat">Trait: {resolvedEquipment.secondaryWeapon.trait}</div>
                      )}
                      {resolvedEquipment.secondaryWeapon.range && (
                        <div className="equipment-stat">Range: {resolvedEquipment.secondaryWeapon.range}</div>
                      )}
                      {resolvedEquipment.secondaryWeapon.damage && (
                        <div className="equipment-stat">Damage: {resolvedEquipment.secondaryWeapon.damage} {resolvedEquipment.secondaryWeapon.damageType}</div>
                      )}
                      {resolvedEquipment.secondaryWeapon.burden && (
                        <div className="equipment-stat">Burden: {resolvedEquipment.secondaryWeapon.burden}</div>
                      )}
                      {resolvedEquipment.secondaryWeapon.feature && (
                        <div className="equipment-feature">Feature: {resolvedEquipment.secondaryWeapon.feature}</div>
                      )}
                    </>
                  ) : (
                    <div className="equipment-loading">Loading weapon details...</div>
                  )}
                </div>
              </div>
              <label className="equipment-toggle">
                <input
                  type="checkbox"
                  checked={equipmentChoices.secondaryWeapon}
                  onChange={(e) => handleEquipmentToggle('secondaryWeapon', e.target.checked)}
                />
                <span className="toggle-text">Add to inventory</span>
              </label>
            </div>
          )}

          {suggestedEquipment.armor && (
            <div className="equipment-choice">
              <div className="equipment-info">
                <h4>Armor</h4>
                <div className="equipment-details">
                  {resolvedEquipment.armor ? (
                    <>
                      <div className="equipment-name">{resolvedEquipment.armor.name}</div>
                      {resolvedEquipment.armor.baseThresholds && (
                        <div className="equipment-stat">
                          Thresholds: {resolvedEquipment.armor.baseThresholds.major}/{resolvedEquipment.armor.baseThresholds.severe}
                        </div>
                      )}
                      {resolvedEquipment.armor.baseArmorScore && (
                        <div className="equipment-stat">Armor Score: {resolvedEquipment.armor.baseArmorScore}</div>
                      )}
                      {resolvedEquipment.armor.feature && (
                        <div className="equipment-feature">Feature: {resolvedEquipment.armor.feature}</div>
                      )}
                    </>
                  ) : (
                    <div className="equipment-loading">Loading armor details...</div>
                  )}
                </div>
              </div>
              <label className="equipment-toggle">
                <input
                  type="checkbox"
                  checked={equipmentChoices.armor}
                  onChange={(e) => handleEquipmentToggle('armor', e.target.checked)}
                />
                <span className="toggle-text">Add to inventory</span>
              </label>
            </div>
          )}
        </div>

        {/* Equipment Summary */}
        <div className="equipment-summary">
          <h3>Equipment Summary</h3>
          <div className="summary-content">
            <div className="summary-section">
              <h4>Selected Equipment:</h4>
              {equipmentChoices.primaryWeapon && (
                <div className="summary-item">✓ {resolvedEquipment.primaryWeapon?.name
                  ? resolvedEquipment.primaryWeapon.name.charAt(0).toUpperCase() + resolvedEquipment.primaryWeapon.name.slice(1)
                  : suggestedEquipment.primaryWeapon.charAt(0).toUpperCase() + suggestedEquipment.primaryWeapon.slice(1)}</div>
              )}
              {equipmentChoices.secondaryWeapon && (
                <div className="summary-item">✓ {resolvedEquipment.secondaryWeapon?.name
                  ? resolvedEquipment.secondaryWeapon.name.charAt(0).toUpperCase() + resolvedEquipment.secondaryWeapon.name.slice(1)
                  : suggestedEquipment.secondaryWeapon!.charAt(0).toUpperCase() + suggestedEquipment.secondaryWeapon!.slice(1)}</div>
              )}
              {equipmentChoices.armor && (
                <div className="summary-item">✓ {resolvedEquipment.armor?.name
                  ? resolvedEquipment.armor.name.charAt(0).toUpperCase() + resolvedEquipment.armor.name.slice(1)
                  : suggestedEquipment.armor.charAt(0).toUpperCase() + suggestedEquipment.armor.slice(1)}</div>
              )}
              {!equipmentChoices.primaryWeapon && !equipmentChoices.secondaryWeapon && !equipmentChoices.armor && (
                <div className="summary-item summary-item--none">No equipment selected</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartingEquipment;